import _ from 'lodash';
import { sqLicenseManagementStore } from '@/core/core.stores';
import { flux } from '@/core/flux.module';
import { LICENSE_STATUS, WARNING_FLAGS } from '@/licenseManagement/licenseManagement.constants.ts';
import { getLicenseLink, goTo } from '@/main/routing.utilities';
import { sqSystemApi } from '@/sdk/api/SystemApi';
import { type LicenseStatusOutputV1, ValidityEnum } from '@/sdk/model/LicenseStatusOutputV1';
import type { AxiosPromise } from 'axios';
import type { LicenseImporterOutputV1 } from '@/sdk/model/LicenseImporterOutputV1';

export function fetchLicense(): Promise<void> {
  return sqSystemApi.getLicense().then(({ data: license }) => {
    const cutOffs = license?.level === 'Trial' ? WARNING_FLAGS.TRIAL : WARNING_FLAGS.STANDARD;
    const isValid = _.matches({ validity: ValidityEnum.Valid });
    const needsNoWarning = _.conforms<LicenseStatusOutputV1>({
      daysToExpiration: (d: number | undefined) => (d ?? 0) > cutOffs.SHOW_ADMIN_WARNING_DAYS_COUNT,
    });
    const needsAdminWarning = _.conforms<LicenseStatusOutputV1>({
      daysToExpiration: (d: number | undefined) => (d ?? 0) > cutOffs.SHOW_WARNING_DAYS_COUNT,
    });
    const status = _.cond<LicenseStatusOutputV1, string>([
      [(input: LicenseStatusOutputV1) => isValid(input) && needsNoWarning(input), _.constant(LICENSE_STATUS.VALID)],
      [
        (input: LicenseStatusOutputV1) => isValid(input) && needsAdminWarning(input),
        _.constant(LICENSE_STATUS.SHOW_ADMIN_LICENSE_WARNING),
      ],
      [isValid, _.constant(LICENSE_STATUS.SHOW_LICENSE_WARNING)],
      [_.matches({ validity: ValidityEnum.Expired }), _.constant(LICENSE_STATUS.EXPIRED)],
      [_.matches({ validity: ValidityEnum.NoLicense }), _.constant(LICENSE_STATUS.NONE)],
      [_.stubTrue, _.constant(LICENSE_STATUS.UNKNOWN)],
    ])(license);

    flux.dispatch('LICENSE_SET_LICENSE', license);
    flux.dispatch('LICENSE_SET_STATUS', { status });
  });
}

export function uploadLicense(file: File): AxiosPromise<LicenseImporterOutputV1> {
  return sqSystemApi.importLicense({ file });
}

export function checkLicenseStatus(currentPath: string): Promise<void> {
  return Promise.resolve(sqLicenseManagementStore.licenseStatus ? undefined : fetchLicense()).then(() => {
    const INVALID_STATUSES = [LICENSE_STATUS.NONE, LICENSE_STATUS.UNKNOWN, LICENSE_STATUS.EXPIRED];
    if (!_.includes(currentPath, 'license') && _.includes(INVALID_STATUSES, sqLicenseManagementStore.licenseStatus)) {
      goTo(getLicenseLink());
    }
  });
}
